import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import {
    PAGE_TOUR_START_EVENT,
    findPageTour,
    isPageTourReady,
    markPageTourSeen,
    readPageTourSeen,
    startPageTour,
} from './page-tours';
import type { TourHandle } from './tour-engine';
import { isWelcomeTourActive, resolveTourSeen } from './welcome-tour';

/** Sayfa verisi bu sure icinde cizilmezse tur bu ziyarette acilmaz. */
export const PAGE_TOUR_READY_TIMEOUT_MS = 5000;
export const PAGE_TOUR_POLL_MS = 250;
/** Hazir hedef gorundukten sonra yerlesimin oturmasi icin kisa bekleme. */
export const PAGE_TOUR_START_DELAY_MS = 400;

type PageTourRequest = { pageId: string; replay: boolean };

type UsePageTourOptions = {
    userId: string;
    /** Otomatik acilis acik mi? (orn. yonetici icin `false`) */
    enabled: boolean;
};

/**
 * Bulunulan sayfanin turunu yonetir. Otomatik acilis kosullari: kullanici
 * yonetici degil, karsilama turu daha once goruldu ve su an acik degil
 * (ilk giriste turlar ust uste binmesin), bu sayfanin turu bu cihazda
 * gorulmedi. Yeniden baslatma istegi (`requestPageTour`) goruldu bilgisine
 * bakmaz. Her iki durumda da tur, sayfanin verisi cizilene kadar bekler.
 */
export const usePageTour = ({ userId, enabled }: UsePageTourOptions) => {
    const { t } = useI18n();
    const { pathname } = useLocation();
    const pageTour = useMemo(() => findPageTour(pathname), [pathname]);
    const [request, setRequest] = useState<PageTourRequest | null>(null);
    const tourRef = useRef<TourHandle | null>(null);
    const leavingRef = useRef(false);

    useEffect(() => {
        if (!pageTour || !enabled || !userId || readPageTourSeen(pageTour.pageId, userId)) return undefined;
        let active = true;
        resolveTourSeen(userId).then((welcomeTourSeen) => {
            if (active && welcomeTourSeen) setRequest({ pageId: pageTour.pageId, replay: false });
        });
        return () => {
            active = false;
        };
    }, [pageTour, enabled, userId]);

    useEffect(() => {
        if (!pageTour) return undefined;
        const requestReplay = () => setRequest({ pageId: pageTour.pageId, replay: true });
        window.addEventListener(PAGE_TOUR_START_EVENT, requestReplay);
        return () => window.removeEventListener(PAGE_TOUR_START_EVENT, requestReplay);
    }, [pageTour]);

    useEffect(() => {
        if (!request || !pageTour || request.pageId !== pageTour.pageId) return undefined;
        const deadline = Date.now() + PAGE_TOUR_READY_TIMEOUT_MS;
        let timer: number | undefined;

        const start = () => {
            setRequest(null);
            if (isWelcomeTourActive()) return;
            tourRef.current?.destroy();
            tourRef.current = startPageTour({
                tour: pageTour,
                t,
                onClose: () => {
                    tourRef.current = null;
                    // Sayfadan ayrilirken kapanan tur "goruldu" sayilmaz.
                    if (!leavingRef.current) markPageTourSeen(pageTour.pageId, userId);
                },
            });
        };

        const waitForPage = () => {
            if (isPageTourReady(pageTour)) {
                timer = window.setTimeout(start, PAGE_TOUR_START_DELAY_MS);
            } else if (Date.now() >= deadline) {
                setRequest(null);
            } else {
                timer = window.setTimeout(waitForPage, PAGE_TOUR_POLL_MS);
            }
        };
        waitForPage();
        return () => window.clearTimeout(timer);
    }, [request, pageTour, t, userId]);

    useEffect(() => {
        // Onceki sayfadan kalan istek baska bir ziyarette tetiklenmesin.
        setRequest(null);
        return () => {
            leavingRef.current = true;
            tourRef.current?.destroy();
            leavingRef.current = false;
        };
    }, [pathname]);
};
